import { Component } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { Tilt } from "./tilt";

const VALUES = [
  {
    icon: "compass",
    title: "Local Expertise",
    copy: "Every route is built by guides who grew up on this island, not assembled from a template.",
  },
  {
    icon: "wallet",
    title: "Honest Pricing",
    copy: "What you see in USD is what you pay - no hidden fees added after you commit.",
  },
  {
    icon: "users",
    title: "Small by Design",
    copy: "Most groups top out around six travelers. It's how the country is actually meant to be seen.",
  },
  {
    icon: "leaf",
    title: "Considered Pace",
    copy: "We'd rather you sit with a place for an hour than tick off a fifth stop before lunch.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Tell us your interests",
    copy: "A short form or an email - dates, pace, what draws you to Sri Lanka in the first place.",
  },
  {
    n: "02",
    title: "We draft a route",
    copy: "Within a day, a real itinerary with real hotels and a real USD price, not a placeholder.",
  },
  {
    n: "03",
    title: "We refine it together",
    copy: "Swap a day, add a hike, slow down the coast leg - it stays flexible until you say go.",
  },
  {
    n: "04",
    title: "You travel",
    copy: "A private guide and vehicle meet you at the airport, and the route we planned unfolds.",
  },
];

const STATS = [
  { value: "11", label: "Years Routing Trips" },
  { value: "2,400+", label: "Travelers Hosted" },
  { value: "6", label: "Signature Routes" },
  { value: "4.9", label: "Average Rating" },
];

@Component({
  selector: "app-about",
  imports: [RouterLink, Icon, Reveal, Tilt],
  template: `
    <section class="grain relative pt-40 pb-28 surface-ink overflow-hidden">
      <div
        class="mesh-blob mesh-blob-a w-[450px] h-[450px] -top-32 -right-24 bg-saffron/15"
      ></div>
      <div
        class="mesh-blob mesh-blob-b w-[320px] h-[320px] bottom-0 left-0 bg-teal/10"
      ></div>
      <div class="container relative z-10">
        <div class="flex items-center gap-2 mb-6" appReveal="fade">
          <div class="w-8 h-[2px] bg-saffron"></div>
          <span
            class="text-saffron font-bold uppercase tracking-[0.3em] text-[10px]"
            >Our Story</span
          >
        </div>
        <h1
          class="text-5xl md:text-7xl font-serif leading-[0.95] tracking-tight max-w-3xl"
          appReveal
          [revealDelay]="0.1"
        >
          A small studio,
          <span class="italic font-light text-paper/50"
            >routing one island.</span
          >
        </h1>
        <p
          class="text-paper/60 max-w-xl mt-6 text-lg leading-relaxed"
          appReveal
          [revealDelay]="0.2"
        >
          Step Into Sri Lanka started with three people comparing notes over tea
          in Kandy - a travel entrepreneur, a tech entrepreneur and a tour
          guide. Eleven years later, we're still that same small team, just a
          better-routed one.
        </p>
      </div>
    </section>

    <section class="py-28">
      <div
        class="container grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
      >
        <div class="relative" appReveal="scale">
          <div
            appTilt
            [appTiltMax]="5"
            class="overflow-hidden rounded-[2.5rem]"
          >
            <img
              src="./home-grid-1.png"
              alt="Waterfall in the Sri Lankan highlands"
              class="w-full h-auto"
              referrerpolicy="no-referrer"
            />
          </div>
          <div
            class="absolute -bottom-8 -right-6 glass rounded-2xl shadow-2xl p-7 hidden sm:block"
          >
            <div class="route-code text-4xl text-vermilion mb-1">CMB</div>
            <div
              class="text-ink/50 text-[10px] uppercase tracking-widest font-bold"
            >
              Where every route begins
            </div>
          </div>
        </div>

        <div class="space-y-6" appReveal [revealDelay]="0.1">
          <span class="route-code text-teal text-xs">HOW IT STARTED</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight">
            We got tired of watching
            <span class="italic font-light text-ink/40"
              >good trips go generic.</span
            >
          </h2>
          <p class="text-ink/65 leading-relaxed text-lg">
            Most visitors were being routed through the same four stops in the
            same order, on itineraries written by someone who'd never driven the
            roads themselves. We started Step Into Sri Lanka to do the opposite
            - every route is walked, timed and priced by the people leading it.
          </p>
          <p class="text-ink/65 leading-relaxed text-lg">
            We're based in Colombo, we work with a small bench of drivers and
            guides we actually trust, and we'd rather turn away a booking that
            doesn't fit than force it onto a route that won't suit you.
          </p>
        </div>
      </div>
    </section>

    <section class="py-28 bg-paper-soft">
      <div class="container">
        <div class="max-w-2xl mb-16" appReveal="fade">
          <span class="route-code text-teal text-xs">WHAT WE HOLD TO</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4">
            Four things we don't compromise on
          </h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          @for (v of values; track v.title; let i = $index) {
            <div
              appTilt
              class="bg-paper rounded-[1.5rem] p-8 border border-sand/60 hover:border-vermilion/40 hover:shadow-2xl transition-all"
              appReveal
              [revealDelay]="i * 0.08"
            >
              <div
                class="w-12 h-12 flex items-center justify-center bg-ink text-saffron rounded-2xl mb-6"
              >
                <app-icon [name]="v.icon" [size]="20" />
              </div>
              <h3 class="font-serif text-xl mb-3">{{ v.title }}</h3>
              <p class="text-ink/55 text-sm leading-relaxed">{{ v.copy }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div class="max-w-2xl mb-16" appReveal="fade">
          <span class="route-code text-teal text-xs">THE PROCESS</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4">
            How we route a trip
          </h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-10">
          @for (s of steps; track s.n; let i = $index) {
            <div appReveal [revealDelay]="i * 0.1">
              <div class="route-code text-5xl text-sand mb-5">{{ s.n }}</div>
              <h3 class="font-serif text-xl mb-3">{{ s.title }}</h3>
              <p class="text-ink/55 text-sm leading-relaxed">{{ s.copy }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <section class="grain relative surface-ink py-24 overflow-hidden">
      <div
        class="mesh-blob mesh-blob-a w-[400px] h-[400px] top-0 left-1/4 bg-vermilion/10"
      ></div>
      <div
        class="container relative z-10 grid grid-cols-2 md:grid-cols-4 gap-8"
      >
        @for (stat of stats; track stat.label; let i = $index) {
          <div class="text-center" appReveal="scale" [revealDelay]="i * 0.08">
            <div class="route-code text-4xl sm:text-5xl text-saffron mb-2">
              {{ stat.value }}
            </div>
            <div
              class="text-paper/50 text-[11px] uppercase tracking-widest font-bold"
            >
              {{ stat.label }}
            </div>
          </div>
        }
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div
          class="relative overflow-hidden bg-paper-soft rounded-[2.5rem] p-14 sm:p-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10"
          appReveal="scale"
        >
          <div class="max-w-xl space-y-5">
            <h2 class="text-4xl sm:text-5xl font-serif leading-tight">
              Ready to see it
              <span class="italic font-light text-ink/40">for yourself?</span>
            </h2>
            <p class="text-ink/60 leading-relaxed">
              Browse the routes we've already mapped, or write in and we'll
              build one from scratch.
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 shrink-0">
            <a routerLink="/tours" class="btn-vermilion justify-center"
              >Browse Tours</a
            >
            <a routerLink="/contact" class="btn-outline justify-center"
              >Get In Touch</a
            >
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class About {
  values = VALUES;
  steps = STEPS;
  stats = STATS;
}
